import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [email, setEmail] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Ugyldig lenke – mangler token');
      setTokenValid(false);
      setValidating(false);
      return;
    }

    // Validate token
    fetch(`${API_BASE_URL}/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setTokenValid(true);
          setEmail(data.email || null);
        } else {
          const data = await res.json().catch(() => ({}));
          setTokenValid(false);
          if (data.code === 'TOKEN_EXPIRED') {
            setError('Lenken har utløpt. Be om en ny tilbakestillingslenke.');
          } else if (data.code === 'TOKEN_USED') {
            setError('Denne lenken er allerede brukt. Be om en ny tilbakestillingslenke.');
          } else {
            setError('Ugyldig eller utløpt lenke.');
          }
        }
      })
      .catch(() => {
        setTokenValid(false);
        setError('Kunne ikke validere lenken. Prøv igjen senere.');
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passordene er ikke like.');
      return;
    }
    if (password.length < 8) {
      setError('Passordet må være minst 8 tegn.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Noe gikk galt. Prøv igjen.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <header className="auth-header">
            <span className="auth-badge">KI Chat</span>
            <h1>Validerer lenke...</h1>
            <p>Vennligst vent mens vi sjekker tilbakestillingslenken din.</p>
          </header>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <header className="auth-header">
            <span className="auth-badge">KI Chat</span>
            <h1>Ugyldig lenke</h1>
            <p>{error || 'Denne lenken er ikke gyldig lenger.'}</p>
          </header>
          <Link to="/login" className="button button-primary">
            Tilbake til innlogging
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <header className="auth-header">
            <span className="auth-badge auth-badge-success">✓</span>
            <h1>Passord oppdatert!</h1>
            <p>Ditt passord er nå endret. Du kan nå logge inn med ditt nye passord.</p>
          </header>
          <Link to="/login" className="button button-primary">
            Gå til innlogging
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <header className="auth-header">
          <span className="auth-badge">KI Chat</span>
          <h1>Velg nytt passord</h1>
          {email && <p>For kontoen: <strong>{email}</strong></p>}
        </header>

        <form className="auth-form" onSubmit={handleSubmit}>
          <label className="auth-label" htmlFor="password">
            Nytt passord
            <input
              id="password"
              name="password"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minst 8 tegn"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          <label className="auth-label" htmlFor="confirmPassword">
            Bekreft passord
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Skriv passordet på nytt"
              autoComplete="new-password"
              required
            />
          </label>
          {error && <div className="auth-error">⚠️ {error}</div>}
          <button type="submit" className="button button-primary" disabled={loading}>
            {loading ? 'Oppdaterer...' : 'Oppdater passord'}
          </button>
        </form>

        <footer className="auth-footer">
          <p>
            <Link to="/login">← Tilbake til innlogging</Link>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
